import { Component, ErrorInfo, PropsWithChildren } from "react";

import { telemetry } from "telemetry";

/**
 * ***Last-resort*** boundary around the whole app.
 *
 * Without it, any throw from code rendered outside a widget's
 * {@link WidgetErrorBoundary} - app-level render hooks such as
 * `useHandleHighlights`, context providers, the HUD - unmounts the entire React
 * root and leaves the user staring at a white screen with no way back.
 *
 * Deliberately dependency-free (no MUI theme, no i18n, no redux): it has to be
 * able to render when the surrounding app is the thing that just failed.
 */
export class RootErrorBoundary extends Component<PropsWithChildren, { hasError: boolean }> {
    constructor(props: PropsWithChildren) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(error, errorInfo);

        telemetry.trackEvent(
            "unhandled_error",
            {
                "error.source": "root_error_boundary",
                "error.component_stack": errorInfo.componentStack ?? "",
            },
            error,
        );
    }

    render() {
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 16,
                        height: "100%",
                        padding: 24,
                        fontFamily: "system-ui, sans-serif",
                        textAlign: "center",
                    }}
                >
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Something went wrong</h2>
                    <p style={{ margin: 0, maxWidth: 480 }}>
                        The viewer hit an unexpected error and had to stop. Reloading usually resolves it.
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{ padding: "8px 20px", fontSize: 14, cursor: "pointer" }}
                    >
                        Reload
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
